<script setup lang="ts">
import { getDirectusAssetURL } from '@@/server/utils/directus-utils';

interface CustomFormData {
	id: string;
	tagline: string | null;
	headline: string | null;
	description?: string | null;
	background_image?: string | { id: string } | null;
	form: CustomForm;
	layout?: 'left' | 'bottom' | 'right';
}
interface CustomForm {
	id: string;
	on_success?: 'redirect' | 'message' | null;
	sort?: number | null;
	submit_label?: string | null;
	success_message?: string | null;
	title?: string | null;
	success_redirect_url?: string | null;
	is_active?: boolean | null;
	fields: FormField[];
}

const { setAttr } = useVisualEditing();
const props = defineProps<{ 
	data: CustomFormData;
	pageId?: string;
	background?: 'light' | 'dark' | null;
}>();

const backgroundImageUrl = computed(() => {
	if (props.data?.background_image) {
		const imageId = typeof props.data.background_image === 'string' 
			? props.data.background_image 
			: props.data.background_image?.id;
		if (imageId) {
			return getDirectusAssetURL(imageId);
		}
	}
	return null;
});

const textColor = computed(() => {
	// Se c'è un'immagine di background, il testo è bianco
	if (backgroundImageUrl.value) {
		return 'text-white';
	}
	// Altrimenti il testo è nero
	return 'text-black';
});

const layout = computed(() => props.data?.layout || 'bottom');
const isBottomLayout = computed(() => layout.value === 'bottom');
const isLeftLayout = computed(() => layout.value === 'left');
const isRightLayout = computed(() => layout.value === 'right');
</script>

<template>
	<section
		v-if="data.form"
		class="relative flex justify-center max-w-full"
		:class="{
			'w-full': !backgroundImageUrl,
			'w-full py-12 md:py-16 lg:py-20': backgroundImageUrl,
		}"
		:style="backgroundImageUrl ? {
			backgroundImage: `url(${backgroundImageUrl})`,
			backgroundSize: 'cover',
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
			...(background === 'dark' ? { backgroundColor: '#000000' } : {}),
		} : {}"
		:data-directus="
			data?.id && backgroundImageUrl
				? setAttr({
						collection: 'block_form',
						item: data.id,
						fields: ['background_image'],
						mode: 'modal',
					})
				: undefined
		"
	>
		<!-- Overlay per migliorare la leggibilità del testo quando c'è un background -->
		<div
			v-if="backgroundImageUrl"
			class="absolute inset-0 bg-black/5 z-0"
		/>
		
		<div
			class="relative z-10 w-full max-w-7xl mx-auto"
			:class="{
				'px-4 sm:px-6': backgroundImageUrl,
			}"
		>
			<!-- Tagline e Headline full width sopra -->
			<div :class="isBottomLayout ? 'text-center' : 'text-left'">
				<Tagline
					v-if="data.tagline"
					:tagline="data.tagline"
					:data-directus="
						setAttr({
							collection: 'block_form',
							item: data.id,
							fields: 'tagline',
							mode: 'popover',
						})
					"
				/>

				<Headline
					v-if="data.headline"
					:headline="data.headline"
					:text-color="textColor"
					:class="isBottomLayout ? 'text-center' : 'text-left'"
					:data-directus="
						setAttr({
							collection: 'block_form',
							item: data.id,
							fields: 'headline',
							mode: 'popover',
						})
					"
				/>
			</div>

			<!-- Layout bottom: description e form in colonna -->
			<template v-if="isBottomLayout">
				<Text
					v-if="data.description"
					:content="data.description"
					:text-color="textColor"
					class="mt-6 max-w-none w-full prose-p:text-center prose-headings:text-center prose-ul:text-center prose-ol:text-center prose-li:text-center prose-blockquote:text-center [&_p]:!mb-2 [&_p+_p]:!mt-2 [&_p]:!text-base [&_p]:!leading-normal [&_ul]:!text-base [&_ul]:!leading-normal [&_ol]:!text-base [&_ol]:!leading-normal [&_li]:!text-base [&_li]:!leading-normal [&_strong]:!text-base [&_b]:!text-base [&_em]:!text-base [&_i]:!text-base"
					:data-directus="
						setAttr({
							collection: 'block_form',
							item: data.id,
							fields: 'description',
							mode: 'drawer',
						})
					"
				/>

				<div
					class="mt-8"
					:data-directus="
						setAttr({
							collection: 'block_form',
							item: data.id,
							fields: ['form'],
							mode: 'popover',
						})
					"
				>
					<FormBuilder :form="data.form" :label-color="textColor" :page-id="pageId" />
				</div>
			</template>

			<!-- Layout left/right: description e form in due colonne su desktop -->
			<template v-else>
				<div class="mt-6 flex flex-col lg:flex-row lg:gap-8 xl:gap-12">
					<!-- Colonna description -->
					<div
						class="flex-1 text-left"
						:class="{
							'lg:order-2': isLeftLayout,
							'lg:order-1': isRightLayout,
						}"
					>
						<Text
							v-if="data.description"
							:content="data.description"
							:text-color="textColor"
							class="max-w-none w-full prose-p:text-left prose-headings:text-left prose-ul:text-left prose-ol:text-left prose-li:text-left prose-blockquote:text-left [&_p]:!mb-2 [&_p+_p]:!mt-2 [&_p]:!text-base [&_p]:!leading-normal [&_ul]:!text-base [&_ul]:!leading-normal [&_ol]:!text-base [&_ol]:!leading-normal [&_li]:!text-base [&_li]:!leading-normal [&_strong]:!text-base [&_b]:!text-base [&_em]:!text-base [&_i]:!text-base"
							:data-directus="
								setAttr({
									collection: 'block_form',
									item: data.id,
									fields: 'description',
									mode: 'drawer',
								})
							"
						/>
					</div>

					<!-- Colonna form -->
					<div
						class="flex-1 mt-8 lg:mt-0"
						:class="{
							'lg:order-1': isLeftLayout,
							'lg:order-2': isRightLayout,
						}"
						:data-directus="
							setAttr({
								collection: 'block_form',
								item: data.id,
								fields: ['form'],
								mode: 'popover',
							})
						"
					>
						<FormBuilder :form="data.form" :label-color="textColor" :page-id="pageId" />
					</div>
				</div>
			</template>
		</div>
	</section>
</template>
